import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Key as KeyIcon,
} from '@mui/icons-material';

interface TwoFactorVerificationProps {
  onVerify: (code: string) => Promise<boolean>;
  onCancel: () => void;
  loading?: boolean;
  error?: string;
}

const TwoFactorVerification: React.FC<TwoFactorVerificationProps> = ({
  onVerify,
  onCancel,
  loading = false,
  error = '',
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (verificationCode.length !== 6) {
      return;
    }

    setIsVerifying(true);
    try {
      await onVerify(verificationCode);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  return (
    <Card 
      elevation={8} 
      sx={{ 
        maxWidth: 400, 
        mx: 'auto', 
        mt: 4,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <SecurityIcon sx={{ fontSize: 48, mb: 2, color: '#ffd700' }} />
          <Typography variant="h5" component="h2" gutterBottom>
            Two-Factor Authentication
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Enter the 6-digit code from your authenticator app
          </Typography>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              '& .MuiAlert-message': { color: 'error.main' },
              bgcolor: 'rgba(255, 255, 255, 0.9)'
            }}
          >
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            value={verificationCode}
            onChange={handleCodeChange}
            placeholder="000000"
            inputProps={{
              maxLength: 6,
              style: {
                textAlign: 'center',
                fontSize: '1.5rem',
                letterSpacing: '0.5rem',
                color: '#333',
                fontWeight: 'bold'
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <KeyIcon sx={{ color: '#666' }} />
                </InputAdornment>
              ),
              sx: {
                bgcolor: 'rgba(255, 255, 255, 0.95)',

                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ffd700',
                  borderWidth: 2,
                },
              }
            }}
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={onCancel}
              variant="outlined"
              fullWidth
              disabled={loading || isVerifying}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.5)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Cancel
            </Button>
            
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading || isVerifying || verificationCode.length !== 6}
              sx={{
                bgcolor: '#ffd700',
                color: '#333',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: '#ffed4e',
                },
                '&:disabled': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              {(loading || isVerifying) ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: '#333' }} />
                  <span>Verifying...</span>
                </Box>
              ) : (
                'Verify'
              )}
            </Button>
          </Box>
        </form>

        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block', 
            textAlign: 'center', 
            mt: 2, 
            opacity: 0.8 
          }}
        >
          Having trouble? Contact your system administrator
        </Typography>
      </CardContent>
    </Card>
  );
};

export default TwoFactorVerification;